import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import moment from "moment";
import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import SpaceMemberProfileModal from "components/page/modal/SpaceMemberProfileModal";

import { useSpaceActivityHistory, useSpaceMember } from "hooks/queries/useUser";

const ContentsWrap = styled.div`
    padding: 4px 0 160px;
    width: 100%;
    max-width: 1080px;
`;

const FilterBar = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(140px, 1fr));
    gap: 16px;
    padding: 18px;
    border-radius: 4px;
    background: var(--color-Base1);

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const Filter = styled.label`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

const Timeline = styled.div`
    margin-top: 32px;
`;

const DateGroup = styled.section`
    & + & {
        margin-top: 32px;
    }
`;

const DateHeading = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-SubBlack)",
})`
    margin-bottom: 12px;
`;

const EventList = styled.div`
    border-top: 1px solid var(--color-Outline);
`;

const EventRow = styled.div`
    display: grid;
    grid-template-columns: 68px 28px minmax(0, 1fr);
    column-gap: 12px;
    align-items: start;
    min-height: 76px;
    padding: 16px 12px;
    border-bottom: 1px solid var(--color-Outline);
`;

const EventTime = styled.time`
    padding-top: 5px;
    color: var(--color-Grey1);
    font-size: 1.2rem;
`;

const EventIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid var(--color-Outline);
    border-radius: 50%;
    background: var(--color-White);
`;

const EventBody = styled.div`
    min-width: 0;
    padding-top: 2px;
`;

const EventSentence = styled.div`
    color: var(--color-SubBlack);
    font-size: 1.4rem;
    line-height: 1.7;
`;

const LinkButton = styled.button`
    display: inline;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--color-Key);
    font: inherit;
    text-align: left;
    text-decoration: underline;
`;

const PlainTarget = styled.span`
    font-weight: 500;
`;

const Preview = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    display: -webkit-box;
    margin-top: 6px;
    overflow: hidden;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

const StateBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
    margin-top: 28px;
    border: 1px solid var(--color-Outline);
    border-radius: 4px;
    background: var(--color-Base1);
`;

const StateText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const PERIOD_OPTIONS = [
    { id: "30", name: "최근 30일" },
    { id: "7", name: "최근 7일" },
    { id: "90", name: "최근 90일" },
    { id: "", name: "전체 기간" },
];

const RESOURCE_OPTIONS = [
    { id: "", name: "전체 영역" },
    { id: "reference", name: "참고문헌" },
    { id: "note", name: "연구노트" },
    { id: "manuscript", name: "논문원고" },
];

const EVENT_OPTIONS = [
    { id: "", name: "전체 이벤트" },
    { id: "reference.created", name: "참고문헌 추가" },
    { id: "reference.review_message_created", name: "참고문헌 리뷰 메시지 추가" },
    { id: "note.created", name: "연구노트 추가" },
    { id: "note.revision_created", name: "연구노트 리비전 생성" },
    { id: "note.review_message_created", name: "연구노트 리뷰 메시지 추가" },
    { id: "manuscript.created", name: "논문원고 추가" },
    { id: "manuscript.revision_created", name: "논문원고 리비전 생성" },
    { id: "manuscript.review_message_created", name: "논문원고 리뷰 메시지 추가" },
];

const RESOURCE_ICON = {
    reference: "sideBarReference",
    note: "sideBarNote",
    manuscript: "sideBarManuscript",
};

const RESOURCE_LABEL = {
    reference: "참고문헌",
    note: "연구노트",
    manuscript: "논문원고",
};

function findEventLink(event, type) {
    return event.links?.find((link) => link.type === type) || null;
}

function buildEventAction(event) {
    if (!event.primary_target.url) return null;

    const revision = findEventLink(event, "revision");
    const thread = findEventLink(event, "review_thread");
    const message = findEventLink(event, "review_message");
    if (!revision && !thread) return null;

    const [pathname, currentSearch = ""] = event.primary_target.url.split("?");
    const params = new URLSearchParams(currentSearch);

    if (revision) {
        params.set("panel", "history");
        params.set("revision", String(revision.target_id));
        return { label: "리비전 보기", url: `${pathname}?${params.toString()}` };
    }

    if (event.resource_type === "reference") {
        const selection = findEventLink(event, "review_selection");
        params.set("view", "pdf");
        if (selection?.page) params.set("page", String(selection.page));
        if (selection?.target_id) params.set("selection", String(selection.target_id));
    } else {
        params.set("panel", "review");
    }
    params.set("thread", String(thread.target_id));
    if (message?.target_id) params.set("message", String(message.target_id));
    return { label: "리뷰 보기", url: `${pathname}?${params.toString()}` };
}

function HistoryTab() {
    const { space_uuid } = useParams();
    const navigate = useNavigate();
    const [period, setPeriod] = useState("30");
    const [memberId, setMemberId] = useState("");
    const [resourceType, setResourceType] = useState("");
    const [eventType, setEventType] = useState("");
    const [profileMember, setProfileMember] = useState(null);

    const params = useMemo(() => {
        const next = { limit: 100 };
        if (period)
            next.from = moment()
                .subtract(Number(period) - 1, "days")
                .format("YYYY-MM-DD");
        if (memberId) next.space_member_id = memberId;
        if (resourceType) next.resource_type = resourceType;
        if (eventType) next.event_type = eventType;
        return next;
    }, [eventType, memberId, period, resourceType]);

    const historyQuery = useSpaceActivityHistory(space_uuid, params);
    const memberQuery = useSpaceMember(space_uuid);
    const events = useMemo(() => historyQuery.data?.pages?.flatMap((page) => page.results) || [], [historyQuery.data]);
    const members = useMemo(
        () => (Array.isArray(memberQuery.data) ? memberQuery.data : memberQuery.data?.results || []),
        [memberQuery.data],
    );
    const memberOptions = useMemo(
        () => [
            { id: "", name: "전체 멤버" },
            ...members.map((member) => ({ id: member.id, name: member.name || member.email })),
        ],
        [members],
    );

    const groupedEvents = useMemo(
        () =>
            events.reduce((groups, event) => {
                const date = moment(event.occurred_at).format("YYYY-MM-DD");
                if (!groups[date]) groups[date] = [];
                groups[date].push(event);
                return groups;
            }, {}),
        [events],
    );

    const actorLink = (event) => (
        <LinkButton type="button" onClick={() => setProfileMember({ ...event.actor, id: event.actor.space_member_id })}>
            {event.actor.name}
        </LinkButton>
    );

    const targetLink = (event) => {
        const action = buildEventAction(event);
        const targetUrl = action?.url || event.primary_target.url;
        return targetUrl ? (
            <LinkButton type="button" onClick={() => navigate(targetUrl)}>
                {event.primary_target.label}
            </LinkButton>
        ) : (
            <PlainTarget>{event.primary_target.label}</PlainTarget>
        );
    };

    const renderSentence = (event) => {
        const actor = actorLink(event);
        const target = targetLink(event);
        switch (event.event_type) {
            case "reference.created":
                return (
                    <>
                        {actor} 님이 참고문헌 {target}을 추가했습니다.
                    </>
                );
            case "reference.review_message_created":
                return (
                    <>
                        {actor} 님이 참고문헌 {target}에 리뷰 메시지를 추가했습니다.
                    </>
                );
            case "note.created":
                return (
                    <>
                        {actor} 님이 연구노트 {target}를 추가했습니다.
                    </>
                );
            case "note.revision_created":
                return (
                    <>
                        {actor} 님이 연구노트 {target}의 리비전 #{event.metadata.revision_seq}을 생성했습니다.
                    </>
                );
            case "note.review_message_created":
                return (
                    <>
                        {actor} 님이 연구노트 {target}에 리뷰 메시지를 추가했습니다.
                    </>
                );
            case "manuscript.created":
                return (
                    <>
                        {actor} 님이 논문원고 {target}를 추가했습니다.
                    </>
                );
            case "manuscript.revision_created":
                return (
                    <>
                        {actor} 님이 논문원고 {target}의 리비전 #{event.metadata.revision_seq}을 생성했습니다.
                    </>
                );
            case "manuscript.review_message_created":
                return (
                    <>
                        {actor} 님이 논문원고 {target}에 리뷰 메시지를 추가했습니다.
                    </>
                );
            default:
                return null;
        }
    };

    const previewFor = (event) =>
        event.metadata.message_preview ||
        event.metadata.selected_text_preview ||
        (event.metadata.node_name ? `파일: ${event.metadata.node_name}` : "");

    return (
        <ContentsWrap>
            <FilterBar>
                <Filter>
                    <SelectBox
                        width="100%"
                        height="36px"
                        textAlign="left"
                        items={PERIOD_OPTIONS}
                        value={period}
                        onChange={setPeriod}
                    />
                </Filter>
                <Filter>
                    <SelectBox
                        width="100%"
                        height="36px"
                        textAlign="left"
                        items={memberOptions}
                        value={memberId}
                        onChange={setMemberId}
                    />
                </Filter>
                <Filter>
                    <SelectBox
                        width="100%"
                        height="36px"
                        textAlign="left"
                        items={RESOURCE_OPTIONS}
                        value={resourceType}
                        onChange={setResourceType}
                    />
                </Filter>
                <Filter>
                    <SelectBox
                        width="100%"
                        height="36px"
                        textAlign="left"
                        items={EVENT_OPTIONS}
                        value={eventType}
                        onChange={setEventType}
                    />
                </Filter>
            </FilterBar>

            {historyQuery.isLoading ? (
                <StateBox>
                    <StateText>활동 기록을 불러오는 중입니다.</StateText>
                </StateBox>
            ) : historyQuery.isError ? (
                <StateBox>
                    <StateText>활동 기록을 불러오지 못했습니다.</StateText>
                </StateBox>
            ) : events.length === 0 ? (
                <StateBox>
                    <StateText>조건에 맞는 활동 기록이 없습니다.</StateText>
                </StateBox>
            ) : (
                <Timeline>
                    {Object.entries(groupedEvents).map(([date, dateEvents]) => (
                        <DateGroup key={date}>
                            <DateHeading>{moment(date).format("YYYY년 M월 D일")}</DateHeading>
                            <EventList>
                                {dateEvents.map((event) => {
                                    const preview = previewFor(event);
                                    return (
                                        <EventRow key={event.id}>
                                            <EventTime>{moment(event.occurred_at).format("HH:mm")}</EventTime>
                                            <Tooltip
                                                message={RESOURCE_LABEL[event.resource_type]}
                                                position="top"
                                                width="28px"
                                            >
                                                <EventIcon>
                                                    <Icon name={RESOURCE_ICON[event.resource_type]} size="14" />
                                                </EventIcon>
                                            </Tooltip>
                                            <EventBody>
                                                <EventSentence>{renderSentence(event)}</EventSentence>
                                                {preview && <Preview>{preview}</Preview>}
                                            </EventBody>
                                        </EventRow>
                                    );
                                })}
                            </EventList>
                        </DateGroup>
                    ))}
                    {historyQuery.hasNextPage && (
                        <Button
                            {...BUTTON_STYLES.modalSecondary}
                            width="120px"
                            margin="24px auto 0"
                            disabled={historyQuery.isFetchingNextPage}
                            onClick={() => historyQuery.fetchNextPage()}
                            buttonText={historyQuery.isFetchingNextPage ? "불러오는 중" : "더 보기"}
                        />
                    )}
                </Timeline>
            )}

            {profileMember && (
                <SpaceMemberProfileModal readOnly member={profileMember} onClose={() => setProfileMember(null)} />
            )}
        </ContentsWrap>
    );
}

export default HistoryTab;
